<template lang="pug">
.outing-table-component
  // テーブル
  b-table(
    :items="_value"
    :fields="fields"
    :current-page="currentPage"
    :per-page="perPage"
    :filter="filter"
    :filter-included-fields="filterFields"
    :busy="isBusy"
    no-border-collapse
    table-class="basic-table nohead-table"
    show-empty
    :filter-function="filterTable"
    @filtered="onFiltered"
  )
    // ローディング
    template(#table-busy)
      .text-center.table-loading-message
        b-spinner.spinner
        span.message.align-self-center 読込み中
    // レコード無し
    template(#empty)
      .empty-message
        span 情報がありません。
    // フィルター後レコード無し
    template(#emptyfiltered)
      .empty-message
        span 情報がありません。

    // セル：予約
    // tableライブラリは使用するが、見た目はtableではないようにする
    template(#cell(outing)="row")
      .outing-row(@click="onDetail(row.item)")
        .d-flex.flex-row.align-items-center.mb-1
          .flex-grow-1
            .date-time.d-flex.align-items-center
              .d-flex.flex-column(v-if="row.item.type == 'goingOut'")
                .d-flex.flex-row
                  span {{ getDate(row.item.startDatetime) }}（
                  span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
                  span {{ getTime(row.item.startDatetime) }}
                  span ～
                  span {{ getTime(row.item.endDatetime) }}
              .d-flex.flex-column(v-if="row.item.type == 'stayingOut'")
                .d-flex.flex-row
                  span {{ getDate(row.item.startDatetime) }}（
                  span(:class="weekCssClass(row.item.startDatetime)") {{ getWeek(row.item.startDatetime) }}）
                  span {{ getTime(row.item.startDatetime) }}
                  span ～
                .d-flex.flex-row
                  span {{ getDate(row.item.endDatetime) }}（
                  span(:class="weekCssClass(row.item.endDatetime)") {{ getWeek(row.item.endDatetime) }}）
                  span {{ getTime(row.item.endDatetime) }}
            .status.status-no-consented(v-if="row.item.consented == false") 未承諾（{{ getInputType(row.item.inputBy) }}）
            .status.status-consented(v-else) 承諾済（{{ getInputType(row.item.inputBy) }}）
            .resident-name {{ row.item.targetResidentInfo.name }}（{{ row.item.targetResidentInfo.roomName }}）
          .buttons.svg-icon
            .angle-right
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";

import { InputBy, OutingGetModel } from "@/commons/services/Outings";

/**
 * ### 面会予約テーブルコンポーネント
 */
@Component({})
export default class OutingTable extends Vue {
  /** 1行あたりの情報配列 */
  @Prop({ required: true })
  value!: Array<OutingTableItem>;
  /** 列名配列 */
  @Prop({ required: true })
  fields!: Array<OutingTableField>;
  /** 全体行数 */
  @Prop({ required: true })
  totalRows!: number;
  /** 現在のページ */
  @Prop({ required: true })
  currentPage!: number;
  /** 1ページあたりの表示行数 */
  @Prop({ required: true })
  perPage!: number;
  /** ローディング表示可否 */
  @Prop({ required: true })
  isBusy!: boolean;
  /** フィルター */
  @Prop()
  filter!: any;
  /** フィルター対象カラム */
  @Prop()
  filterFields!: string[];

  get _value(): Array<OutingTableItem> {
    return this.value;
  }

  /**
   * ### 日付(yyyy年mm月dd日)を返す
   * */
  public getDate(datetime: number): string {
    return this.$dateUtil.convertNumberToString(datetime, "mm月dd日");
  }

  /**
   * ### 曜日を返す
   */
  public getWeek(datetime: number): string {
    const dateObject: Date = this.$dateUtil.convertNumberToDate(datetime);
    return this.$dateUtil.getWeekName(dateObject, "ja");
  }

  /**
   * ### 時分を返す
   */
  public getTime(datetime: number): string {
    return this.$dateUtil.convertNumberToString(datetime, "hh:MM");
  }

  /**
   * ### 曜日の文字色CssClassの割り当て
   */
  public weekCssClass(datetime: number): string {
    let weekClassName: string = "";
    const weekName: string = this.getWeek(datetime);
    switch (weekName) {
      case "日":
        weekClassName = "sunday";
        break;
      case "土":
        weekClassName = "saturday";
        break;
      default:
        break;
    }

    return weekClassName;
  }

  /**
   * ### 入力種別を返す
   */
  public getInputType(inputBy: InputBy): string {
    if (inputBy == InputBy.FACILITY) {
      return "代理届け";
    } else {
      return "本人届け";
    }
  }

  /**
   * ### フィルタリング
   */
  public filterTable(item: OutingTableItem): boolean {
    if (item.targetResidentInfo.groupId == this.filter) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### フィルタリングイベント
   */
  public onFiltered(filteredItems: any) {
    this.$emit("update:totalRows", filteredItems.length);
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: OutingTableItem): void {
    this.$emit("onDetail", item);
    return;
  }
}

/** テーブルの列名の情報 */
export interface OutingTableField {
  key: string;
  label: string;
  sortable: boolean;
  sortDirection: string;
  class?: string;
}

/** テーブルの1行あたりの情報 */
export interface OutingTableItem extends OutingGetModel {}
</script>

<style lang="scss" scoped>
@import "../../../styles/mobile/_mixin.scss";
@import "../../../styles/mobile/_colors.scss";

.outing-table-component {
  .svg-icon {
    width: 16px;
    height: 16px;
    margin-left: -6px;
    .angle-right {
      @include svg-icon("../../../assets/angle-right.svg", $font-base, 18px, 18px);
    }
  }
  .icon-offline {
    @include svg-icon("../../../assets/reservation/offline.svg", $font-base, 24px, 24px);
  }
  .icon-online {
    @include svg-icon("../../../assets/reservation/online.svg", $font-base, 24px, 24px);
  }

  .sunday {
    color: $font-sunday;
  }
  .saturday {
    color: $font-saturday;
  }
  .status-consented {
    color: $font-disable;
    font-weight: bold;
  }
  .status-no-consented {
    color: $font-strong;
    font-weight: bold;
  }
  .resident-name {
    font-weight: bold;
  }
  .buttons {
    .angle-right {
      color: $font-base;
    }
  }
}
</style>
